/**
 * Per-section data layer of the new Deal Insights page (EXCH-19435).
 *
 * Every dashboard section runs its own async BQ job (one `POST /deal_insights` per section) with its own
 * cache, loading and error state. React Query's abort `signal` guarantees a stale job never replaces current
 * data: when the key changes or the last observer unmounts, polling stops and the job is cancelled.
 *
 * It lives alongside the old `DealInsights/` (one job per tab) until release, after which the old directory
 * is removed.
 */
import { type BqJobResponseApi, bqJobStatus } from '@openx/types/bqJob.ts';
import type {
  DealInsightsApi,
  DealInsightsChartInputApi,
  DealInsightsCreateJobIdParamsApi,
} from '@openx/types/dealInsights.ts';
import { QueryKeys } from '@openx/utils/api/queryKeys.ts';
import { BQ_JOB_REFETCH_INTERVAL } from '@openx/utils/services/config.ts';
import { useQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useDealContext } from '../../utils/context/DealContext.tsx';
import { defaultFetchError } from '../../utils/handleBqJobResponse.ts';

const CACHE_TTL = 30 * 60 * 1000;
// 1 minute with the default refetch interval
const STILL_PROCESSING_POLL_COUNT = 60;

const abortableSleep = (ms: number, signal: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    const onAbort = () => {
      clearTimeout(timeout);
      reject(signal.reason);
    };
    const timeout = setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    if (signal.aborted) {
      onAbort();
      return;
    }
    signal.addEventListener('abort', onAbort, { once: true });
  });

type UseDealInsightsSectionParams<K extends keyof DealInsightsChartInputApi & keyof DealInsightsApi> = {
  section: K;
  input: NonNullable<DealInsightsChartInputApi[K]>;
  queryParams: Omit<DealInsightsCreateJobIdParamsApi, 'charts'>;
  enabled?: boolean;
};

export const useDealInsightsSection = <K extends keyof DealInsightsChartInputApi & keyof DealInsightsApi>({
  section,
  input,
  queryParams,
  enabled = true,
}: UseDealInsightsSectionParams<K>) => {
  const { useDealInsightsJobApi } = useDealContext();
  const { createJob, getJob, cancelJob } = useDealInsightsJobApi();
  const { enqueueSnackbar } = useSnackbar();

  const { data, isLoading, error, refetch } = useQuery({
    enabled,
    queryKey: [QueryKeys.DEAL_INSIGHTS_SECTION, section, queryParams, input],
    queryFn: async ({ signal }): Promise<DealInsightsApi[K]> => {
      const jobId = await createJob({ ...queryParams, charts: { [section]: input } });
      let cancelled = false;
      const cancel = () => {
        if (!cancelled) {
          cancelled = true;
          cancelJob(jobId).catch(() => undefined);
        }
      };
      // cancel right on abort, even while a `getJob` request is still pending
      signal.addEventListener('abort', cancel, { once: true });

      try {
        // aborted while `createJob` was pending: the listener above missed it
        if (signal.aborted) {
          cancel();
        }
        signal.throwIfAborted();

        for (let pollCount = 1; ; pollCount++) {
          await abortableSleep(BQ_JOB_REFETCH_INTERVAL, signal);
          const { status, output, msg }: BqJobResponseApi<DealInsightsApi> = await getJob(jobId);
          signal.throwIfAborted();

          switch (status) {
            case bqJobStatus.Ready:
              if (!output) {
                throw new Error(defaultFetchError);
              }
              return output[section];
            case bqJobStatus.Error:
              throw new Error(msg ?? defaultFetchError);
            case bqJobStatus.InProgress:
            case bqJobStatus.Running:
              if (pollCount === STILL_PROCESSING_POLL_COUNT) {
                enqueueSnackbar('Data is still being processed. This may take longer than expected.', {
                  variant: 'info',
                });
              }
              break;
            default:
              throw new Error(`Request status(${status}) unknown.`);
          }
        }
      } finally {
        signal.removeEventListener('abort', cancel);
      }
    },
    staleTime: CACHE_TTL,
    gcTime: CACHE_TTL,
    retry: false,
  });

  return { data, isLoading, error, refetch };
};
